import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getStripeCardDetails,
  getStripeSubscriptionHistory,
} from "../api/apiFunctions";
import RenewSubscription from "../components/RenewSubscription";
import SaveCardForm from "../components/SaveCardForm";
import SubscriptionPaymentSection from "../components/SubscriptionPaymentSection";
import SubscriptionPackages from "./SubscriptionPackages";
import { useSubscriptionAccess } from "../context/SubscriptionAccessContext";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

export default function AddSubscription() {
  const navigate = useNavigate();
  const [selectedPackageId, setSelectedPackageId] = useState(() => {
    return localStorage.getItem("selectedPackageId") || null;
  });
  const [isCardSaved, setIsCardSaved] = useState(() => {
    return localStorage.getItem("isCardSaved") === "true";
  });
  const [initializing, setInitializing] = useState(true);
  const { subscriptionStatus, setSubscriptionStatus } = useSubscriptionAccess();

  useEffect(() => {
    if (selectedPackageId) {
      localStorage.setItem("selectedPackageId", selectedPackageId);
    }
  }, [selectedPackageId]);

  useEffect(() => {
    localStorage.setItem("isCardSaved", isCardSaved);
  }, [isCardSaved]);

  useEffect(() => {
    const bootstrap = async () => {
      const token =
        localStorage.getItem("authToken") ||
        sessionStorage.getItem("authToken");

      if (!token) {
        setInitializing(false);
        return;
      }

      let cardSaved = false;
      try {
        // Card save status
        try {
          const cardRes = await getStripeCardDetails(token);
          cardSaved = Boolean(cardRes.cardDetails);
          setIsCardSaved(cardSaved);
        } catch (error) {
          cardSaved = false;
          setIsCardSaved(false);
        }

        // Subscription status
        try {
          const history = await getStripeSubscriptionHistory(token);
          const subscription = history.subscriptions?.[0];
          if (!subscription) {
            setSubscriptionStatus("never");
          } else if (
            subscription.status === "active" &&
            subscription.isActive
          ) {
            setSubscriptionStatus("active");
            // Only redirect if card is saved
            if (cardSaved) {
              navigate("/patient-dashboard");
            }
          } else if (
            subscription.status === "cancelled" &&
            !subscription.isActive
          ) {
            setSubscriptionStatus("expired");
          } else {
            setSubscriptionStatus("never");
          }
        } catch {
          setSubscriptionStatus("never");
        }
      } finally {
        setInitializing(false);
      }
    };

    bootstrap();
  }, [navigate, setSubscriptionStatus]);

  const handleSelectPackage = (pkgId) => {
    setSelectedPackageId(pkgId);
  };

  const handleCardSaved = () => {
    setIsCardSaved(true);
  };

  const handleSubscriptionCompleted = () => {
    setSubscriptionStatus("active");
    navigate("/patient-dashboard");
  };

  // card will remain save, but the subscription forms will show up again
  const handleRenewSubscription = () => {
    setSelectedPackageId(null);
    setSubscriptionStatus("never");
  };

  if (initializing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2d348f]"></div>
      </div>
    );
  }

  if (subscriptionStatus === "expired") {
    return <RenewSubscription onRenew={handleRenewSubscription} />;
  }

  if (subscriptionStatus === "never") {
    return (
      <>
        {!selectedPackageId ? (
          <SubscriptionPackages
            selectedId={selectedPackageId}
            onSelect={handleSelectPackage}
          />
        ) : (
          <Elements stripe={stripePromise}>
            {!isCardSaved ? (
              <SaveCardForm onCardSaved={handleCardSaved} />
            ) : (
              <SubscriptionPaymentSection
                selectedPackageId={selectedPackageId}
                onCompleted={handleSubscriptionCompleted}
              />
            )}
          </Elements>
        )}
      </>
    );
  }
}
